/*
 * Copyright 2018 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import Touch from './Touch';

export default class TouchRenderer {
  constructor(props) {
    this.setup(props);
  }

  setup = props => {
    this.setupProperties(props);
    this.setupCamera();
    this.setupTouches();
    this.setupEvents();
  };

  setupProperties = props => {
    this.renderer = props.renderer;
    this.canvas = props.canvas;
    this.FSMode = false;
  };

  setupCamera = () => {
    let size = this.renderer.getDrawingBufferSize();
    this.camera = new THREE.OrthographicCamera(
      0,
      size.width,
      size.height,
      0,
      -1,
      1
    );
  };

  setupTouches = props => {
    this.touches = new THREE.Scene();
    this.touchesActive = [];
    this.touchesCache = [];
    this.touchesHash = {};
    this.maxTouches = 5;
    for (let i = 0; i < this.maxTouches; i++) {
      let touch = new Touch({
        touchColor: new THREE.Color(0xffffff),
        touchAlpha: 0.5,
        size: 256,
      });
      this.touches.add(touch);
      this.touchesCache.push(touch);
    }
  };

  setupEvents = () => {
    this.canvas.addEventListener('touchstart', this.touchStart, false);
    this.canvas.addEventListener('touchmove', this.touchMove, false);
    this.canvas.addEventListener('touchend', this.touchEnd, false);
    this.canvas.addEventListener('touchcancel', this.touchEnd, false);
    let fsEvent = event => {
      this.FSMode = !this.FSMode;
    };
    document.addEventListener('fullscreenchange', fsEvent);
    document.addEventListener('webkitfullscreenchange', fsEvent);
    document.addEventListener('mozfullscreenchange', fsEvent);
  };

  touchStart = event => {
    let canvasRect = this.canvas.getBoundingClientRect();
    let size = this.renderer.getDrawingBufferSize();
    let touches = event.touches;
    let keys = Object.keys(touches);
    keys.forEach(key => {
      let touch = touches[key];
      let id = touch.identifier;
      let touchObject = undefined;
      if (this.touchesHash[id] === undefined) {
        this.touchesHash[id] = this.touchesCache.pop();
      }
      touchObject = this.touchesHash[id];
      let x;
      let y;
      if (this.FSMode) {
        x = size.width * touch.clientX / window.innerWidth;
        y = size.height * (1.0 - touch.clientY / window.innerHeight);
      } else {
        x = size.width * (touch.clientX - canvasRect.left) / canvasRect.width;
        y =
          size.height *
          (1.0 - (touch.clientY - canvasRect.top) / canvasRect.height);
      }
      touchObject.position.set(x, y, 0);
      touchObject.fadeIn(33);
    });
  };

  touchMove = event => {
    let canvasRect = this.canvas.getBoundingClientRect();
    let size = this.renderer.getDrawingBufferSize();
    let touches = event.touches;
    let keys = Object.keys(touches);

    keys.forEach(key => {
      let touch = touches[key];
      let id = touch.identifier;
      let touchObject = this.touchesHash[id];
      if (touchObject) {
        let x;
        let y;
        if (this.FSMode) {
          x = size.width * touch.clientX / window.innerWidth;
          y = size.height * (1.0 - touch.clientY / window.innerHeight);
        } else {
          x = size.width * (touch.clientX - canvasRect.left) / canvasRect.width;
          y =
            size.height *
            (1.0 - (touch.clientY - canvasRect.top) / canvasRect.height);
        }
        touchObject.position.set(x, y, 0);
      }
    });
  };

  touchEnd = event => {
    let canvasRect = this.canvas.getBoundingClientRect();
    let size = this.renderer.getDrawingBufferSize();
    let touches = event.changedTouches;
    let keys = Object.keys(touches);

    keys.forEach(key => {
      let touch = touches[key];
      let id = touch.identifier;
      let touchObject = this.touchesHash[id];
      let x;
      let y;
      if (this.FSMode) {
        x = size.width * touch.clientX / window.innerWidth;
        y = size.height * (1.0 - touch.clientY / window.innerHeight);
      } else {
        x = size.width * (touch.clientX - canvasRect.left) / canvasRect.width;
        y =
          size.height *
          (1.0 - (touch.clientY - canvasRect.top) / canvasRect.height);
      }
      touchObject.position.set(x, y, 0);
      touchObject.fadeOut(33);
      this.touchesCache.push(touchObject);
      this.touchesHash[id] = undefined;
      delete this.touchesHash[id];
    });
  };

  resize = () => {
    this.setupCamera();
  };

  render = () => {
    this.renderer.render(this.touches, this.camera);
  };
}



// WEBPACK FOOTER //
// ./src/gfx/TouchRenderer.js